import {
  SHOPIFY_CA_PKCE_VERIFIER_KEY,
  SHOPIFY_CA_TOKENS_KEY,
} from "@constants/storageKeys"
import {
  getLocalStorage,
  getSessionStorage,
  removeLocalStorage,
  writeLocalStorage,
  writeSessionStorage,
} from "@utils/storage"

const CLIENT_ID = process.env.GATSBY_SHOPIFY_CUSTOMER_CLIENT_ID as string
const CUSTOMER_SCOPES = "openid email customer-account-api:full"
const REDIRECT_URI = "https://urbanarmorgear.com/api/authCallback"
const SHOP_STOREFRONT_DOMAIN =
  (process.env.GATSBY_SHOPIFY_SHOP_CUSTOM_DOMAIN as string) ||
  (process.env.GATSBY_SHOPIFY_SHOP_DOMAIN as string)

type OidcConfig = {
  authorization_endpoint: string
  end_session_endpoint: string
  graphql_api?: string
  issuer: string
  jwks_uri: string
  token_endpoint: string
}

let _oidc: null | OidcConfig = null

type TokenSet = {
  access_token: string
  expires_in: number
  id_token?: string
  obtained_at: number
  refresh_token: string
}

export function clearTokens() {
  removeLocalStorage(SHOPIFY_CA_TOKENS_KEY)
}

/**
 * Customer Account API GraphQL fetcher
 * Add new hosts in Shopify "JavaScript origin(s)"
 */
export async function customerAccountFetch<T>(
  query: string,
  variables?: Record<string, unknown>
): Promise<T> {
  await refreshIfNeeded()
  const t = getTokens()
  if (!t) throw new Error("Missing customer token")

  const cfg = await getOidcConfig()
  const endpoint = cfg.graphql_api || buildCaGraphqlFromIssuer(cfg.issuer)

  const r = await fetch(endpoint, {
    body: JSON.stringify({ query, variables }),
    headers: {
      accept: "application/json",
      Authorization: `${t.access_token}`,
      "content-type": "application/json",
    },
    method: "POST",
  })
  const json = await r.json().catch(() => ({}))
  // Helpful debugging and safer failure path
  if (!r.ok) {
    console.error("[CA] GraphQL HTTP error:", r.status, json)
    throw new Error(`CA GraphQL ${r.status}`)
  }
  if ((json as any)?.errors?.length) {
    console.error("[CA] GraphQL errors:", (json as any).errors)
  }
  return json as T
}

export function getAccessToken(): null | string {
  const bag = getLocalStorage<any>(SHOPIFY_CA_TOKENS_KEY) as any
  return bag?.access_token || null
}

export function getTokens(): null | TokenSet {
  return (
    (getLocalStorage<TokenSet>(SHOPIFY_CA_TOKENS_KEY) as null | TokenSet) ||
    null
  )
}

/**
 * Handle OAuth callback on /auth/callback/
 * (after the proxy has bounced the user back with ?code and ?state)
 */
export async function handleCallback(qs: URLSearchParams) {
  const code = qs.get("code")
  if (!code) throw new Error("Missing code")

  const cfg = await getOidcConfig() // ← NEW
  // Try localStorage (with TTL), then sessionStorage. If neither, bail with a clear message.
  let verifier =
    (getLocalStorage<string>(SHOPIFY_CA_PKCE_VERIFIER_KEY) as string) || ""
  if (!verifier && typeof window !== "undefined") {
    verifier = getSessionStorage(SHOPIFY_CA_PKCE_VERIFIER_KEY) || ""
  }
  if (!verifier) {
    throw new Error(
      "Missing PKCE verifier — did the login start on a different origin or a new tab?"
    )
  }

  const body = new URLSearchParams()
  body.set("grant_type", "authorization_code")
  body.set("client_id", CLIENT_ID)
  body.set("redirect_uri", REDIRECT_URI)
  body.set("code", code)
  body.set("code_verifier", verifier)

  const resp = await fetch(cfg.token_endpoint, {
    body,
    headers: {
      "content-type": "application/x-www-form-urlencoded",
      origin: window.location.origin,
      "user-agent": navigator.userAgent,
    },
    method: "POST",
  })
  if (!resp.ok) throw new Error(`Token exchange failed: ${resp.status}`)
  const { access_token, expires_in, id_token, refresh_token } =
    await resp.json()
  setTokens({ access_token, expires_in, id_token, refresh_token })
}

/**
 * Log out of Customer Accounts. If we have an id_token, use Shopify's logout endpoint
 * so the hosted session is cleared as well; otherwise just clear local state.
 */
export async function logout(
  returnPath = window.location.pathname + window.location.search
) {
  const t = getTokens()
  clearTokens()

  try {
    const cfg = await getOidcConfig()
    // If we don’t have an id_token (rare), just client-redirect
    if (!t?.id_token) {
      const state = toB64u({ rp: returnPath, rt: window.location.origin })
      const localBounce = new URL("/api/authLogout", window.location.origin)
      localBounce.searchParams.set("state", state)
      window.location.assign(localBounce.toString())
      return
    }

    const postLogout = "https://urbanarmorgear.com/api/authLogout"
    const u = new URL(cfg.end_session_endpoint)
    u.searchParams.set("id_token_hint", t.id_token)
    u.searchParams.set("post_logout_redirect_uri", postLogout)

    // OIDC RP-logout supports `state`; Shopify preserves it back to our post_logout URI
    const state = toB64u({ rp: returnPath, rt: window.location.origin })
    u.searchParams.set("state", state)

    window.location.assign(u.toString())
  } catch {
    // Fallback
    window.location.assign("/")
  }
}

/**
 * Refresh the CA access token if it's close to expiry.
 * Soft-fails by clearing tokens on refresh error.
 */
export async function refreshIfNeeded() {
  const t = getTokens()
  if (!t || !expired(t)) return

  const cfg = await getOidcConfig()
  const body = new URLSearchParams()
  body.set("grant_type", "refresh_token")
  body.set("client_id", CLIENT_ID)
  body.set("refresh_token", t.refresh_token)

  const resp = await fetch(cfg.token_endpoint, {
    body,
    headers: {
      "content-type": "application/x-www-form-urlencoded",
      origin: window.location.origin,
      "user-agent": navigator.userAgent,
    },
    method: "POST",
  })
  if (!resp.ok) {
    clearTokens()
    return
  }
  const { access_token, expires_in, refresh_token } = await resp.json()
  setTokens({ access_token, expires_in, refresh_token })
}

/**
 * Start OAuth (PKCE).
 * - state.rt = current origin so the proxy can bounce back to the right host
 * - state.rp = optional return path (e.g., "/rewards" or "/account")
 */
export async function startLogin(returnPath?: string) {
  const cfg = await getOidcConfig()
  const origin = window.location.origin

  // Sanitize optional return path → must start with "/" to be used
  const rp =
    typeof returnPath === "string" && returnPath.startsWith("/")
      ? returnPath
      : undefined

  const stateObj: { rp?: string; rt: string } = { rt: origin }
  if (rp) stateObj.rp = rp

  const state = btoa(JSON.stringify(stateObj))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "")

  const verifier = b64u(crypto.getRandomValues(new Uint8Array(32)).buffer)
  const challenge = await sha256(verifier)

  // Persist PKCE verifier in both local & session storage
  writeLocalStorage(SHOPIFY_CA_PKCE_VERIFIER_KEY, verifier, 10 * 60 * 1000)
  writeSessionStorage(SHOPIFY_CA_PKCE_VERIFIER_KEY, verifier)

  const url = new URL(cfg.authorization_endpoint)
  url.searchParams.set("scope", CUSTOMER_SCOPES)
  url.searchParams.set("client_id", CLIENT_ID)
  url.searchParams.set("response_type", "code")
  url.searchParams.set("redirect_uri", REDIRECT_URI)
  url.searchParams.set("code_challenge", challenge)
  url.searchParams.set("code_challenge_method", "S256")
  url.searchParams.set("state", state)

  window.location.assign(url.toString())
}

function b64u(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let s = ""
  for (let i = 0; i < bytes.length; i++) {
    s += String.fromCharCode(bytes[i])
  }
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "")
}

function buildCaGraphqlFromIssuer(issuer: string): string {
  // Issuer: https://shopify.com/authentication/{shop_id}
  const u = new URL(issuer)
  const parts = u.pathname.split("/").filter(Boolean)
  const shopId = parts[1] // ["authentication", "{shop_id}"]
  if (!shopId) throw new Error("Cannot derive shop_id from OIDC issuer")
  const apiVersion =
    (process.env.GATSBY_SHOPIFY_API_VERSION as string) || "2025-07"
  // Customer Account GraphQL endpoint (no .json)
  return `https://shopify.com/${shopId}/account/customer/api/${apiVersion}/graphql`
}

function expired(t: TokenSet) {
  const age = (Date.now() - t.obtained_at) / 1000
  return age > t.expires_in - 60
}

async function getOidcConfig(): Promise<OidcConfig> {
  if (_oidc) return _oidc

  // Call our Netlify Function to avoid CORS
  const u = new URL("/api/authOidc", window.location.origin)
  if (SHOP_STOREFRONT_DOMAIN) {
    u.searchParams.set("domain", SHOP_STOREFRONT_DOMAIN)
  }

  const res = await fetch(u.toString(), { credentials: "omit", method: "GET" })
  if (!res.ok) {
    throw new Error(`OIDC discovery failed: ${res.status}`)
  }
  _oidc = (await res.json()) as OidcConfig
  return _oidc
}

function setTokens(t: Omit<TokenSet, "obtained_at">) {
  writeLocalStorage(SHOPIFY_CA_TOKENS_KEY, { ...t, obtained_at: Date.now() })
}

async function sha256(s: string) {
  return b64u(
    await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s))
  )
}

function toB64u(obj: any) {
  const json = JSON.stringify(obj)
  return btoa(json).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "")
}
